import React from 'react';
import PropTypes from 'prop-types';
import { CardHeader, Tooltip } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GetAppIcon from '@mui/icons-material/GetApp';

import { DataGrid, PageTitleWithToolTip } from '../Common';
import { text_commonCellProps } from '../Common/DataGrid/utils';
import { getDateRangeFilterOperator } from '../Common/DataGrid/FilterComponents';
import Routes from '../../routes';
import { Grey2 } from '../../color_utils';
import { FormatDate } from '../../utils';
import useViewport from '../../hooks/useViewport';
import '../Common/Common.css';

const dateRangeFilterOperator = getDateRangeFilterOperator({});

const LocationRegistration = ({
    id, // locationID
    refreshData,
    onEdit,
    onDelete,
    onDocumentClick
}) => {
    // US 16739: reactive so the mobile exclusions update on resize/orientation.
    const { isMobile } = useViewport();

    const columns = [
        { field: 'locationRegistrationTypeName', headerName: 'Location Registration Type', ...text_commonCellProps },
        { field: 'facilityID', headerName: 'Facility ID', ...text_commonCellProps },
        { field: 'startDate', headerName: 'Start Date', filterOperators: dateRangeFilterOperator, flex: 1, renderCell: ({ value }) => FormatDate(value) },
        { field: 'endDate', headerName: 'End Date', filterOperators: dateRangeFilterOperator, flex: 1, renderCell: ({ value }) => FormatDate(value) },
        { field: 'createdBy', headerName: 'Created By', ...text_commonCellProps },
        { field: 'creationDate', headerName: 'Creation Date', filterOperators: dateRangeFilterOperator, flex: 1, renderCell: ({ value }) => FormatDate(value) },
    ];

    const actionRow = {
        type: 'actions',
        field: 'Quick Actions',
        width: 140,
        getActions: ({ row }) => {
            // US 16739 (QA): download the attachments directly — the old
            // View-Attachments browser modal wasn't responsive and exposed Delete.
            const actions = [
                <GridActionsCellItem
                    key="download"
                    label="Download Attachments"
                    disabled={!row.hasFile}
                    icon={<Tooltip title="Download Attachments"><GetAppIcon sx={{ color: Grey2 }} /></Tooltip>}
                    onClick={() => onDocumentClick(row)}
                />,
            ];
            // US 16739: Registrations are view-only on the mobile breakpoint — the
            // Download action stays, Edit + Delete are excluded.
            if (!isMobile) {
                actions.push(
                    <GridActionsCellItem
                        key="edit"
                        label="Edit Location Registration"
                        icon={<Tooltip title="Edit Location Registration"><EditIcon sx={{ color: Grey2 }} /></Tooltip>}
                        onClick={() => onEdit(row)}
                    />,
                    <GridActionsCellItem
                        key="delete"
                        label="Delete Location Registration"
                        icon={<Tooltip title="Delete Location Registration"><DeleteIcon sx={{ color: Grey2 }} /></Tooltip>}
                        onClick={() => onDelete(row)}
                    />,
                );
            }
            return actions;
        },
    };

    return (
        <DataGrid
            hideExportButton
            url={Routes.Location.LocationRegistrationTable(id)}
            refresh={refreshData}
            getRowId={(row) => row.registrationID}
            noResultText="Oops...It is empty here..."
            noResultText2="Create a new Location Registration to get started."
            columns={[...columns, actionRow]}
            headerContent={
                <CardHeader
                    title={
                        <PageTitleWithToolTip
                            pageTitle="Registrations"
                            toolTipText="A location's different registrations can be added and dates tracked into Trakref"
                        />
                    }
                    sx={{
                        "& .MuiCardHeader-action": { display: "flex" },
                        "& .MuiCardHeader-content": { maxWidth: "100%", textWrap: "nowrap" }
                    }}
                />
            }
        />
    );
};

LocationRegistration.propTypes = {
    id: PropTypes.string.isRequired,
    refreshData: PropTypes.any.isRequired
};

export default LocationRegistration;
